/**
 * マスタ管理サービス（フロントエンド向けアダプター）
 * hooks から userId を意識せず呼び出せるよう、Firebase Auth の currentUser を自動取得する。
 * 各エンティティの Firestore 実装は個別サービスファイルに委譲する。
 */
import { getAuth } from 'firebase/auth';
import type {
  LimitType, PaymentDate, Unit, Bank,
  BusinessType, TradeType, EmployeeCategory, Employee, Usergroup,
} from '../types';
import * as limitTypeSvc from './limitType.service';
import * as paymentDateSvc from './paymentDate.service';
import * as unitSvc from './unit.service';
import * as bankSvc from './bank.service';
import * as businessTypeSvc from './businessType.service';
import * as tradeTypeSvc from './tradeType.service';
import * as employeeCategorySvc from './employeeCategory.service';
import * as employeeSvc from './employee.service';
import * as usergroupSvc from './usergroup.service';
import * as companyProfileSvc from './companyProfile.service';
import type { CompanyProfile } from '../types';

// 型エイリアス（hooks の既存型と互換）
export type LimitTypeInput = limitTypeSvc.LimitTypeCreateInput;
export type PaymentDateInput = paymentDateSvc.PaymentDateCreateInput;
export type UnitInput = unitSvc.UnitCreateInput;
export type BankInput = bankSvc.BankCreateInput;
export type BusinessTypeInput = businessTypeSvc.BusinessTypeCreateInput;
export type TradeTypeInput = tradeTypeSvc.TradeTypeCreateInput;
export type EmployeeCategoryInput = employeeCategorySvc.EmployeeCategoryCreateInput;
export type EmployeeInput = employeeSvc.EmployeeCreateInput;
export interface EmployeeFilters { usergroupId?: string; categoryId?: string; }
export type UsergroupInput = usergroupSvc.UsergroupCreateInput;
export type CompanyProfileInput = companyProfileSvc.CompanyProfileInput;

function getUserId(): string {
  const uid = getAuth().currentUser?.uid;
  if (!uid) throw new Error('ログインが必要です。');
  return uid;
}

// --- LimitType ---
export const fetchLimitTypes = (): Promise<LimitType[]> =>
  limitTypeSvc.fetchLimitTypes() as Promise<LimitType[]>;
export const createLimitType = (data: LimitTypeInput): Promise<LimitType> =>
  limitTypeSvc.createLimitType(data, getUserId()) as Promise<LimitType>;
export const updateLimitType = (id: string, data: Partial<LimitTypeInput>): Promise<LimitType> =>
  limitTypeSvc.updateLimitType(id, data, getUserId()) as Promise<LimitType>;
export const deleteLimitType = (id: string): Promise<void> =>
  limitTypeSvc.deleteLimitType(id, getUserId());
export const reorderDefaultLimitTypes = (orderedIds: string[]): Promise<void> =>
  limitTypeSvc.reorderDefaultLimitTypes(orderedIds, getUserId());

// --- PaymentDate ---
export const fetchPaymentDates = (): Promise<PaymentDate[]> =>
  paymentDateSvc.fetchPaymentDates() as Promise<PaymentDate[]>;
export const createPaymentDate = (data: PaymentDateInput): Promise<PaymentDate> =>
  paymentDateSvc.createPaymentDate(data, getUserId()) as Promise<PaymentDate>;
export const updatePaymentDate = (id: string, data: Partial<PaymentDateInput>): Promise<PaymentDate> =>
  paymentDateSvc.updatePaymentDate(id, data, getUserId()) as Promise<PaymentDate>;
export const deletePaymentDate = (id: string): Promise<void> =>
  paymentDateSvc.deletePaymentDate(id, getUserId());
export const reorderDefaultPaymentDates = (orderedIds: string[]): Promise<void> =>
  paymentDateSvc.reorderDefaultPaymentDates(orderedIds, getUserId());

// --- Unit ---
export const fetchUnits = (): Promise<Unit[]> =>
  unitSvc.fetchUnits() as Promise<Unit[]>;
export const createUnit = (data: UnitInput): Promise<Unit> =>
  unitSvc.createUnit(data, getUserId()) as Promise<Unit>;
export const updateUnit = (id: string, data: Partial<UnitInput>): Promise<Unit> =>
  unitSvc.updateUnit(id, data, getUserId()) as Promise<Unit>;
export const deleteUnit = (id: string): Promise<void> =>
  unitSvc.deleteUnit(id, getUserId());
export const reorderDefaultUnits = (orderedIds: string[]): Promise<void> =>
  unitSvc.reorderDefaultUnits(orderedIds, getUserId());

// --- Bank ---
export const fetchBanks = (): Promise<Bank[]> =>
  bankSvc.fetchBanks() as Promise<Bank[]>;
export const createBank = (data: BankInput): Promise<Bank> =>
  bankSvc.createBank(data, getUserId()) as Promise<Bank>;
export const updateBank = (id: string, data: Partial<BankInput>): Promise<Bank> =>
  bankSvc.updateBank(id, data, getUserId()) as Promise<Bank>;
export const deleteBank = (id: string): Promise<void> =>
  bankSvc.deleteBank(id, getUserId());
export const setDefaultBank = (id: string): Promise<void> =>
  bankSvc.setDefaultBank(id, getUserId());

// --- BusinessType ---
export const fetchBusinessTypes = (): Promise<BusinessType[]> =>
  businessTypeSvc.fetchBusinessTypes() as Promise<BusinessType[]>;
export const createBusinessType = (data: BusinessTypeInput): Promise<BusinessType> =>
  businessTypeSvc.createBusinessType(data, getUserId()) as Promise<BusinessType>;
export const updateBusinessType = (id: string, data: Partial<BusinessTypeInput>): Promise<BusinessType> =>
  businessTypeSvc.updateBusinessType(id, data, getUserId()) as Promise<BusinessType>;
export const deleteBusinessType = (id: string): Promise<void> =>
  businessTypeSvc.deleteBusinessType(id, getUserId());

// --- TradeType ---
export const fetchTradeTypes = (): Promise<TradeType[]> =>
  tradeTypeSvc.fetchTradeTypes() as Promise<TradeType[]>;
export const createTradeType = (data: TradeTypeInput): Promise<TradeType> =>
  tradeTypeSvc.createTradeType(data, getUserId()) as Promise<TradeType>;
export const updateTradeType = (id: string, data: Partial<TradeTypeInput>): Promise<TradeType> =>
  tradeTypeSvc.updateTradeType(id, data, getUserId()) as Promise<TradeType>;
export const deleteTradeType = (id: string): Promise<void> =>
  tradeTypeSvc.deleteTradeType(id, getUserId());
export const reorderDefaultTradeTypes = (orderedIds: string[]): Promise<void> =>
  tradeTypeSvc.reorderDefaultTradeTypes(orderedIds, getUserId());

// --- EmployeeCategory ---
export const fetchEmployeeCategories = (): Promise<EmployeeCategory[]> =>
  employeeCategorySvc.fetchEmployeeCategories() as Promise<EmployeeCategory[]>;
export const createEmployeeCategory = (data: EmployeeCategoryInput): Promise<EmployeeCategory> =>
  employeeCategorySvc.createEmployeeCategory(data, getUserId()) as Promise<EmployeeCategory>;
export const updateEmployeeCategory = (id: string, data: Partial<EmployeeCategoryInput>): Promise<EmployeeCategory> =>
  employeeCategorySvc.updateEmployeeCategory(id, data, getUserId()) as Promise<EmployeeCategory>;
export const deleteEmployeeCategory = (id: string): Promise<void> =>
  employeeCategorySvc.deleteEmployeeCategory(id, getUserId());

// --- Employee ---
export const fetchEmployees = (filters?: EmployeeFilters): Promise<Employee[]> =>
  employeeSvc.fetchEmployees(filters) as Promise<Employee[]>;
export const createEmployee = (data: EmployeeInput): Promise<Employee> =>
  employeeSvc.createEmployee(data, getUserId()) as Promise<Employee>;
export const updateEmployee = (id: string, data: Partial<EmployeeInput>): Promise<Employee> =>
  employeeSvc.updateEmployee(id, data, getUserId()) as Promise<Employee>;
export const deleteEmployee = (id: string): Promise<void> =>
  employeeSvc.deleteEmployee(id, getUserId());

// --- Usergroup ---
export const fetchUsergroups = (): Promise<Usergroup[]> =>
  usergroupSvc.fetchUsergroups() as Promise<Usergroup[]>;
export const createUsergroup = (data: UsergroupInput): Promise<Usergroup> =>
  usergroupSvc.createUsergroup(data, getUserId()) as Promise<Usergroup>;
export const updateUsergroup = (id: string, data: Partial<UsergroupInput>): Promise<Usergroup> =>
  usergroupSvc.updateUsergroup(id, data, getUserId()) as Promise<Usergroup>;
export const deleteUsergroup = (id: string): Promise<void> =>
  usergroupSvc.deleteUsergroup(id, getUserId());

// --- CompanyProfile（自社情報・単一レコード） ---
export const fetchCompanyProfile = (): Promise<CompanyProfile | null> =>
  companyProfileSvc.fetchCompanyProfile();
export const saveCompanyProfile = (data: CompanyProfileInput): Promise<CompanyProfile> =>
  companyProfileSvc.saveCompanyProfile(data, getUserId());
